import React, { useState, useEffect } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, TextField, FormControlLabel, Checkbox, Box, Stack,
} from '@mui/material';
import { Shift } from '../../interfaces/shift/Shift';
import { ShiftAssignment, CreateAssignmentRequest } from '../../interfaces/shift/ShiftAssignment';
import { ShiftAssignmentService } from '../../services/shift-assignment.service';
import { useToast } from '../../context/ToastContext';

import SearchableSelect from '../common/SearchableSelect';
import { toLocalIsoDate } from '../../utils/dateUtils';
interface Props {
    open: boolean;
    onClose: () => void;
    userId: string;
    userName: string;
    institutionId: string;
    shifts: Shift[];
    existingAssignment: ShiftAssignment | null;
    onSaved: () => void;
}

const today = () => toLocalIsoDate();

const AssignmentEditDialog: React.FC<Props> = ({
    open, onClose, userId, userName, institutionId, shifts, existingAssignment, onSaved,
}) => {
    const toast = useToast();
    const [shiftId, setShiftId] = useState('');
    const [effectiveFrom, setEffectiveFrom] = useState(today());
    const [effectiveTo, setEffectiveTo] = useState('');
    const [openEnded, setOpenEnded] = useState(true);
    const [replaceExisting, setReplaceExisting] = useState(false);
    const [saving, setSaving] = useState(false);
    const [errors, setErrors] = useState<{ shiftId?: string; effectiveFrom?: string; effectiveTo?: string }>({});

    useEffect(() => {
        if (open) {
            setShiftId(existingAssignment?.shiftId ?? '');
            setEffectiveFrom(today());
            setEffectiveTo('');
            setOpenEnded(true);
            setReplaceExisting(existingAssignment != null);
            setErrors({});
        }
    }, [open, existingAssignment]);

    const validate = (): boolean => {
        const errs: typeof errors = {};
        if (!shiftId) errs.shiftId = 'Shift is required';
        if (!effectiveFrom) errs.effectiveFrom = 'Effective from is required';
        if (!openEnded && effectiveTo && effectiveTo < effectiveFrom) {
            errs.effectiveTo = 'Must be on or after effective from';
        }
        setErrors(errs);
        return Object.keys(errs).length === 0;
    };

    const handleSubmit = async () => {
        if (!validate()) return;
        setSaving(true);
        try {
            const body: CreateAssignmentRequest = {
                userId,
                shiftId,
                effectiveFrom,
                effectiveTo: openEnded ? null : (effectiveTo || null),
                replaceExisting,
            };
            await ShiftAssignmentService.create(institutionId, body);
            toast.showSuccess('Assignment saved');
            onSaved();
            onClose();
        } catch (err) {
            toast.showApiError(err, 'Failed to save assignment');
        } finally {
            setSaving(false);
        }
    };

    const activeShifts = shifts.filter(s => s.isActive);

    return (
        <Dialog open={open} onClose={onClose} PaperProps={{ 'data-help-page': 'shifts.assign.page' } as any} maxWidth="sm" fullWidth>
            <DialogTitle>Edit Shift — {userName}</DialogTitle>
            <DialogContent>
                <Box sx={{ pt: 1 }}>
                    <Stack data-help-section="shifts.assign.section.details" spacing={2}>
                        <SearchableSelect
                            options={activeShifts}
                            value={shiftId}
                            onChange={id => { setShiftId(id); setErrors(prev => ({ ...prev, shiftId: undefined })); }}
                            getOptionLabel={s => `${s.name} (${s.code})`}
                            data-help="shifts.assign.field.shift" label="Shift"
                            required
                            error={!!errors.shiftId}
                            helperText={errors.shiftId || undefined}
                            data-testid="assignment-shift-select"
                        />

                        <TextField
                            data-help="shifts.assign.field.effectiveFrom" label="Effective From"
                            type="date"
                            required
                            fullWidth
                            value={effectiveFrom}
                            onChange={e => { setEffectiveFrom(e.target.value); setErrors(prev => ({ ...prev, effectiveFrom: undefined })); }}
                            error={!!errors.effectiveFrom}
                            helperText={errors.effectiveFrom}
                            InputLabelProps={{ shrink: true }}
                            inputProps={{ 'data-testid': 'assignment-effective-from' }}
                        />

                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={openEnded}
                                    onChange={e => setOpenEnded(e.target.checked)}
                                    data-testid="assignment-open-ended"
                                />
                            }
                            data-help="shifts.assign.field.openEnded" label="Open-ended (no end date)"
                        />

                        {!openEnded && (
                            <TextField
                                data-help="shifts.assign.field.effectiveTo" label="Effective To"
                                type="date"
                                fullWidth
                                value={effectiveTo}
                                onChange={e => { setEffectiveTo(e.target.value); setErrors(prev => ({ ...prev, effectiveTo: undefined })); }}
                                error={!!errors.effectiveTo}
                                helperText={errors.effectiveTo}
                                InputLabelProps={{ shrink: true }}
                                inputProps={{ 'data-testid': 'assignment-effective-to' }}
                            />
                        )}

                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={replaceExisting}
                                    onChange={e => setReplaceExisting(e.target.checked)}
                                    data-testid="assignment-replace-existing"
                                />
                            }
                            data-help="shifts.assign.field.replace" label="End-date prior assignment (replace existing)"
                        />
                    </Stack>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button data-help="shifts.assign.cancel" onClick={onClose} disabled={saving}>Cancel</Button>
                <Button onClick={handleSubmit} variant="contained" disabled={saving} data-help="shifts.assign.save" data-testid="assignment-save-btn">
                    {saving ? 'Saving...' : 'Save'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default AssignmentEditDialog;
